import type { ReactNode } from 'react';

import styles from './Kbd.module.css';

/**
 * One key, drawn as a key. Not exported: every caller wants a whole chord, so
 * `KbdKeys` below is the way in, and this stays a private piece of it until
 * something genuinely needs a lone chip.
 *
 * A chip per key rather than one chip holding "⌘⇧E": the row then reads as the
 * things a hand actually does. What goes in it comes from `device.ts`, which
 * knows how the keyboard in front of the reader spells itself.
 *
 * Deliberately quiet — a hint sits next to a control, and the control is the
 * thing being pointed at. No monospace, even though `--font-mono` exists for
 * code spans in a message: this is chrome pointing at a button, not something
 * anybody typed, and it sits in a row of the product's own type.
 */
function Kbd({ children }: { children: ReactNode }) {
  return <kbd className={styles.kbd}>{children}</kbd>;
}

/**
 * A shortcut's keys in a row.
 *
 * The gap between keys lives in here rather than in each hint's own layout,
 * which is how it should have been written the first time: the composer's two
 * hints set it separately, drifted a pixel apart, and the same shortcut ended up
 * spaced two ways in one composer. Anything that shows a chord uses this, and
 * there is one number.
 *
 * A note for whatever sits beside one of these: the words carry the row. A bare
 * chord against a round button reads as two shapes arguing, and the fix turned
 * out to be the sentence rather than the geometry — "⌘E to expand" is plainly a
 * phrase about keys, so the chips stop competing with the control and the
 * composer's two discs can go on being a pair.
 */
export function KbdKeys({ keys }: { keys: string[] }) {
  return (
    <span className={styles.keys}>
      {keys.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </span>
  );
}
